import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { CONSOLE } from "@/constants/testIds";
import { ArrowUpRight, ArrowDownRight, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Operations() {
  const { token, actions } = useApp();
  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Operations</div>
        <h1 className="text-3xl font-semibold tracking-tight">Mint, Burn & Minter Allowance</h1>
      </div>

      <Tabs defaultValue="mint" className="w-full">
        <TabsList className="bg-white border border-slate-200 rounded-md p-1">
          <TabsTrigger value="mint" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-sm px-4">Mint</TabsTrigger>
          <TabsTrigger value="burn" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-sm px-4">Burn</TabsTrigger>
          <TabsTrigger value="minter" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-sm px-4">Configure Minter</TabsTrigger>
        </TabsList>

        <TabsContent value="mint" className="mt-6">
          <MintForm token={token} onSubmit={actions.doMint} />
        </TabsContent>
        <TabsContent value="burn" className="mt-6">
          <BurnForm token={token} onSubmit={actions.doBurn} />
        </TabsContent>
        <TabsContent value="minter" className="mt-6">
          <ConfigureMinterForm token={token} onSubmit={actions.doConfigureMinter} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function FormShell({ icon: Icon, title, description, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md max-w-2xl">
      <div className="border-b border-slate-200 px-5 py-4 flex items-center gap-3">
        <div className="w-8 h-8 bg-slate-900 text-white flex items-center justify-center rounded-sm">
          <Icon className="w-4 h-4" strokeWidth={1.75} />
        </div>
        <div>
          <h3 className="text-sm font-semibold tracking-tight">{title}</h3>
          <p className="text-xs text-slate-500">{description}</p>
        </div>
      </div>
      <div className="px-5 py-5 space-y-4">{children}</div>
    </div>
  );
}

function MintForm({ token, onSubmit }) {
  const [to, setTo] = useState("");
  const [amount, setAmount] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  return (
    <FormShell icon={ArrowUpRight} title={`Mint new ${token.symbol}`} description="Caller must hold MINTER_ROLE. Mint decreases your remaining allowance.">
      <div className="space-y-2">
        <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Recipient address</Label>
        <Input data-testid={CONSOLE.mintToInput} value={to} onChange={(e) => setTo(e.target.value)} placeholder="0x…" className="font-mono" />
      </div>
      <div className="space-y-2">
        <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Amount ({token.symbol})</Label>
        <Input data-testid={CONSOLE.mintAmountInput} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="1000.00" inputMode="decimal" className="font-mono" />
      </div>
      <label className="flex items-start gap-2 text-xs text-slate-600">
        <Checkbox checked={confirm} onCheckedChange={(v) => setConfirm(Boolean(v))} />
        <span>I confirm this mint is backed 1:1 by reserves and complies with the issuance policy.</span>
      </label>
      <Button
        data-testid={CONSOLE.mintSubmitBtn}
        disabled={!confirm || !to || !amount || loading}
        onClick={async () => {
          setLoading(true);
          try { await onSubmit({ to, amount }); setTo(""); setAmount(""); setConfirm(false); }
          catch (e) { toast.error("Mint failed", { description: e.message }); }
          finally { setLoading(false); }
        }}
        className="rounded-md bg-slate-900 hover:bg-slate-800 text-white"
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ArrowUpRight className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} />}
        Submit mint transaction
      </Button>
    </FormShell>
  );
}

function BurnForm({ token, onSubmit }) {
  const [from, setFrom] = useState("");
  const [amount, setAmount] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  return (
    <FormShell icon={ArrowDownRight} title={`Burn ${token.symbol}`} description="Caller must hold BURNER_ROLE. Leave 'from' blank to burn from caller; otherwise an ERC-20 allowance is required.">
      <div className="space-y-2">
        <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">From (optional)</Label>
        <Input data-testid={CONSOLE.burnFromInput} value={from} onChange={(e) => setFrom(e.target.value)} placeholder="leave blank to burn from self" className="font-mono" />
      </div>
      <div className="space-y-2">
        <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Amount ({token.symbol})</Label>
        <Input data-testid={CONSOLE.burnAmountInput} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="500.00" inputMode="decimal" className="font-mono" />
      </div>
      <label className="flex items-start gap-2 text-xs text-slate-600">
        <Checkbox checked={confirm} onCheckedChange={(v) => setConfirm(Boolean(v))} />
        <span>I confirm this burn matches a redeemed reserve and reduces circulating supply.</span>
      </label>
      <Button
        data-testid={CONSOLE.burnSubmitBtn}
        disabled={!confirm || !amount || loading}
        onClick={async () => {
          setLoading(true);
          try { await onSubmit({ from, amount }); setFrom(""); setAmount(""); setConfirm(false); }
          catch (e) { toast.error("Burn failed", { description: e.message }); }
          finally { setLoading(false); }
        }}
        className="rounded-md bg-red-600 hover:bg-red-700 text-white"
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ArrowDownRight className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} />}
        Submit burn transaction
      </Button>
    </FormShell>
  );
}

function ConfigureMinterForm({ token, onSubmit }) {
  const [minter, setMinter] = useState("");
  const [allowance, setAllowance] = useState("");
  const [loading, setLoading] = useState(false);

  return (
    <FormShell icon={Plus} title="Configure minter allowance" description="Caller must hold MASTER_MINTER_ROLE. Sets or replaces the per-minter mint allowance.">
      <div className="space-y-2">
        <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Minter address</Label>
        <Input data-testid={CONSOLE.configureMinterAddr} value={minter} onChange={(e) => setMinter(e.target.value)} placeholder="0x…" className="font-mono" />
      </div>
      <div className="space-y-2">
        <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Allowance ({token.symbol})</Label>
        <Input data-testid={CONSOLE.configureMinterAllowance} value={allowance} onChange={(e) => setAllowance(e.target.value)} placeholder="1000000" inputMode="decimal" className="font-mono" />
      </div>
      <Button
        data-testid={CONSOLE.configureMinterBtn}
        disabled={!minter || !allowance || loading}
        onClick={async () => {
          setLoading(true);
          try { await onSubmit({ minter, allowance }); setMinter(""); setAllowance(""); }
          catch (e) { toast.error("Configure failed", { description: e.message }); }
          finally { setLoading(false); }
        }}
        className="rounded-md bg-slate-900 hover:bg-slate-800 text-white"
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} />}
        Save allowance
      </Button>
    </FormShell>
  );
}
